import { GroupShared } from "pages/OrganizationAdmin/types";
import React, { useEffect, useMemo, useState, memo } from "react";
import { Tables, Typography, showToast } from "@deeptrue-frontend/ui";
import Search from "components/Fields/Search";
import api from "api";
import { UserType } from "pages/OrganizationAdmin/Users";
import User from "./User";
import { UserShared } from "pages/OrganizationAdmin/types";
import { useFormatMessage } from "components/IntlProvider";

const { Table, TableBody, TableHeader } = Tables;

interface Props {
  groupData: GroupShared;
  isLoaded: boolean;
  organizationCodename: string;
  updateGroup: (groupData: GroupShared) => void;
}

const Users = memo(
  ({ groupData, organizationCodename, updateGroup, isLoaded }: Props) => {
    const [searchedUsers, setSearchedUsers] = useState([]);
    const [groupUsers, setGroupUsers] = useState<any[]>([]);
    const t = useFormatMessage();

    const getUsers = (searchValue?: string) => {
      return api.resources.organization.users
        .getOrganizationUsers({
          organizationCodename: organizationCodename,
          params: {
            search: searchValue,
          },
        })
        .then(({ data }) => {
          const users = data.results.map((userData: UserType) => ({
            id: userData.user.id,
            label: `${userData.user.first_name} ${userData.user.last_name} (${userData.user.email})`,
          }));

          setSearchedUsers(users);
        });
    };

    useEffect(() => {
      getUsers();
      // eslint-disable-next-line
    }, []);

    useEffect(() => {
      setGroupUsers(groupData.users);
    }, [groupData.users]);

    const addUserToGroup = (id: number) => {
      const currentUsers = groupUsers.map((user) => user.id);

      return api.resources.organization.groups
        .updateGroup({
          organizationCodename: organizationCodename,
          groupId: groupData.id as number,
          data: {
            users: [...currentUsers, id],
          },
        })
        .then(({ data }) => {
          updateGroup(data);
          showToast({
            type: "success",
            title: t("organization.addedTo", "{x} added to {y}", {
              x: t("organization.user", "User"),
              y: t("organization.group", "group"),
            }),
          });
        });
    };

    const removeUserfromGroup = (id: number) => {
      const currentUsers = groupUsers
        .map((user) => user.id)
        .filter((user) => {
          return user !== id;
        });

      return api.resources.organization.groups
        .updateGroup({
          organizationCodename: organizationCodename,
          groupId: groupData.id as number,
          data: {
            users: currentUsers,
          },
        })
        .then(({ data }) => {
          updateGroup(data);
          showToast({
            type: "success",
            title: t("organization.removedFrom", "{x} removed from {y}", {
              x: t("organization.user", "User"),
              y: t("organization.group", "group"),
            }),
          });
        });
    };

    const filteredOptions = useMemo(() => {
      return searchedUsers.filter(
        (searchedUser: { id: number; label: string }) => {
          const itemIndex = groupUsers.findIndex((user) => {
            return user.id === searchedUser.id;
          });

          return itemIndex === -1;
        }
      );
    }, [groupUsers, searchedUsers]);

    return (
      <>
        <Typography fontWeight={600} mb="1rem">
          {t("organization.addTo", "Add {x} to {y}", {
            y: t("organization.group", "Group"),
            x: t("organization.users", "Users"),
          })}
        </Typography>
        <Search
          options={filteredOptions}
          styles={{
            maxWidth: "450px",
          }}
          name="user"
          onChange={(_name, val) => {
            if (val) {
              addUserToGroup(val.id);
            }
          }}
          placeholder={t("organization.searchValue", "Search {name}", {
            name: t("organization.users", "Users"),
          })}
          asyncSeach={(searchedValue: string) => getUsers(searchedValue)}
        />
        <Typography fontWeight={600} mb="1rem" mt="2rem">
          {t("organization.userInGroups", "Users in group")}:
        </Typography>
        <Table>
          <TableHeader
            cells={[
              {
                text: t("organization.columns.name", "Name"),
                props: {
                  padding: "1rem 0.5rem",
                  height: "3rem !important",
                },
              },
              {
                text: "Email",
                props: {
                  padding: "1rem 0.5rem",
                  height: "3rem !important",
                },
              },
              {
                text: t("organization.columns.resources", "Resources"),
                props: {
                  padding: "1rem 0.5rem",
                  height: "3rem !important",
                },
              },
            ]}
          />

          <TableBody
            isLoading={!isLoaded}
            withLoader
            isData={groupData.users.length > 0}
          >
            {groupUsers.map((user: UserShared) => {
              return (
                <User
                  id={user.id}
                  displayName={`${user.first_name} ${user.last_name}`}
                  email={user.email}
                  key={user.email}
                  removeUser={removeUserfromGroup}
                />
              );
            })}
          </TableBody>
        </Table>
      </>
    );
  }
);

export default Users;
